import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import api from '../api/api';
import { logger } from '../utils/logger';

// Create the RecruiterContext
export const RecruiterContext = createContext();

export const RecruiterProvider = ({ children }) => {
  const { isAuthenticated, user: authUser } = useContext(AuthContext);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSeats, setAvailableSeats] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);

  // Fetch recruiters data when authentication state changes
  useEffect(() => {
    if (isAuthenticated && authUser && authUser.userType === 'employer') {
      fetchRecruiters();
      fetchSubscriptionDetails();
    } else {
      setRecruiters([]);
      setAvailableSeats(0);
    }
  }, [isAuthenticated, authUser]);

  // Function to fetch recruiters
  const fetchRecruiters = useCallback(async () => {
    if (!isAuthenticated || authUser?.userType !== 'employer') {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Log the request for debugging
      logger.debug('Fetching recruiters from:', '/recruiters');
      
      // Fix the API endpoint path - remove the /api prefix to avoid double prefix issues
      const response = await api.get('/recruiters');
      logger.debug('Recruiters API response:', response.data);
      
      // Process recruiters to filter out removed ones
      const processRecruiters = (recruitersArray) => {
        if (!recruitersArray || !Array.isArray(recruitersArray)) return [];
        // Filter out recruiters with 'removed' status
        return recruitersArray.filter(recruiter => recruiter.status !== 'removed');
      };
      
      // Handle different response structures
      if (response.data && response.data.success && response.data.data) {
        // New API response structure with success and data wrapper
        if (response.data.data.recruiters) {
          setRecruiters(processRecruiters(response.data.data.recruiters));
        } else if (Array.isArray(response.data.data)) {
          setRecruiters(processRecruiters(response.data.data));
        } else {
          // Data exists but not in expected format
          logger.debug('API returned data in unexpected format:', response.data.data);
          setRecruiters(processRecruiters(response.data.data.recruiters || []));
        }
      } else if (response.data && response.data.recruiters) {
        // Original expected format
        setRecruiters(processRecruiters(response.data.recruiters));
      } else if (Array.isArray(response.data)) {
        // Array format
        setRecruiters(processRecruiters(response.data));
      } else {
        // If response structure is unexpected, use mock data
        console.warn('Unexpected API response structure:', response.data);
        throw new Error('Unexpected API response structure');
      }
    } catch (err) {
      console.error('Failed to fetch recruiters:', err);
      setError('Failed to fetch recruiters. Using mock data for demonstration.');
      
      // Use mock data for demo purposes
      setRecruiters([
        {
          id: 'rec-001',
          name: 'Alex Johnson',
          email: 'alex.johnson@example.com',
          status: 'active',
          joinedAt: '2025-09-01T10:30:00Z',
          lastActiveAt: '2025-09-30T15:45:00Z',
          loginHours: {
            today: 5.2,
            thisWeek: 24.5,
            thisMonth: 98.3,
            total: 120.5
          }
        },
        {
          id: 'rec-002',
          name: 'Sarah Miller',
          email: 'sarah.miller@example.com',
          status: 'active',
          joinedAt: '2025-08-15T14:20:00Z',
          lastActiveAt: '2025-09-29T17:30:00Z',
          loginHours: {
            today: 3.5,
            thisWeek: 18.5,
            thisMonth: 85.7,
            total: 150.2
          }
        },
        {
          id: 'rec-003',
          name: 'Michael Brown',
          email: 'michael.brown@example.com',
          status: 'pending',
          joinedAt: null,
          lastActiveAt: null,
          loginHours: {
            today: 0,
            thisWeek: 0,
            thisMonth: 0,
            total: 0
          }
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authUser]);

  // Function to fetch subscription details
  const fetchSubscriptionDetails = useCallback(async () => {
    if (!isAuthenticated || authUser?.userType !== 'employer') {
      return;
    }
    
    try {
      // PRIMARY: Fetch from usage stats API for real-time plan data
      logger.debug('Fetching plan limits from usage stats API');
      
      const usageResponse = await api.get('/usage/stats');
      
      if (usageResponse.data.success) {
        const totalSeats = usageResponse.data.data.limits.recruiterSeats;
        const usedSeats = usageResponse.data.data.usage.recruiterSeats;
        
        logger.debug('✅ Got real plan limits:', { totalSeats, usedSeats });
        
        setTotalSeats(totalSeats);
        setAvailableSeats(Math.max(0, totalSeats - usedSeats));
      } else {
        throw new Error('Usage stats API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
      
      // Fallback: Try billing API
      try {
        logger.debug('Fallback: Trying billing API');
        const response = await api.get('/billing/subscription');
        
        if (response.data && response.data.success && response.data.data) {
          const totalSeats = response.data.data.totalSeats || 0;
          const availableSeats = response.data.data.availableSeats || 0;
          
          logger.debug('Setting seats from billing API:', { totalSeats, availableSeats });
          setTotalSeats(totalSeats);
          setAvailableSeats(availableSeats);
        } else if (response.data) {
          const totalSeats = response.data.totalSeats || 0;
          const availableSeats = response.data.availableSeats || 0;
          
          setTotalSeats(totalSeats);
          setAvailableSeats(availableSeats);
        } else {
          throw new Error('Billing API returned unexpected structure');
        }
      } catch (billingErr) {
        console.error('Failed to fetch from billing API:', billingErr);
        
        // Last resort: Count active recruiters
        const activeRecruiters = recruiters.filter(r => r.status === 'active' || r.status === 'pending').length;
        
        logger.debug('⚠️ Using fallback - counting active recruiters:', { activeRecruiters });
        
        // Set minimal seats based on current recruiters
        setTotalSeats(Math.max(1, activeRecruiters));
        setAvailableSeats(0);
      }
    }
  }, [isAuthenticated, authUser, recruiters]);

  // Function to invite a recruiter
  const inviteRecruiter = useCallback(async (email, name) => {
    if (!isAuthenticated || authUser?.userType !== 'employer') {
      setError('You must be logged in as an employer to invite recruiters');
      return false;
    }
    
    // Count active and pending recruiters
    const activeRecruiters = recruiters.filter(r => r.status === 'active' || r.status === 'pending').length;
    
    // Calculate available seats directly to ensure accuracy
    const calculatedAvailableSeats = Math.max(0, totalSeats - activeRecruiters);
    
    logger.debug('Invite check:', { availableSeats, calculatedAvailableSeats, totalSeats, activeRecruiters });
    
    if (calculatedAvailableSeats <= 0) {
      setError('No available seats in your current plan. Please upgrade to add more recruiters.');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Fix the API endpoint path - remove the /api prefix to avoid double prefix issues
      logger.debug('Sending invitation request for:', { email, name });
      const response = await api.post('/recruiters/invite', { email, name });
      logger.debug('Invitation response:', response.data);
      
      // Check if the response indicates success
      if (response.data && response.data.success) {
        // Log the invite link if available
        if (response.data.inviteLink) {
          logger.debug('Invitation link:', response.data.inviteLink);
        }
        
        // Check if this was a re-invitation
        const wasReInvited = response.data.reInvited === true;
        
        // Refresh the recruiters list and subscription details
        await fetchRecruiters();
        await fetchSubscriptionDetails();
        
        // Return success with re-invitation flag
        return { success: true, reInvited: wasReInvited };
      } else {
        // Log detailed error information
        console.error('API returned error response:', response.data);
        setError(response.data?.message || 'Failed to invite recruiter');
        return false;
      }
    } catch (err) {
      console.error('Failed to invite recruiter:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          baseURL: err.config?.baseURL,
          headers: err.config?.headers,
          data: err.config?.data
        }
      });
      
      // Handle specific error codes
      if (err.response?.status === 409) {
        // 409 Conflict - Email already exists
        const errorMessage = 'A recruiter with this email already exists and is currently active. You cannot re-invite an active recruiter.';
        setError(errorMessage);
      } else {
        // Other errors
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to invite recruiter';
        setError(errorMessage);
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authUser, availableSeats, fetchRecruiters, fetchSubscriptionDetails]);

  // Function to remove a recruiter
  const removeRecruiter = useCallback(async (recruiterId) => {
    if (!isAuthenticated || authUser?.userType !== 'employer') {
      setError('You must be logged in as an employer to remove recruiters');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      logger.debug(`Removing recruiter with ID: ${recruiterId}`);
      // Fix the API endpoint path - remove the /api prefix to avoid double prefix issues
      const response = await api.delete(`/recruiters/${recruiterId}`);
      logger.debug('Remove recruiter response:', response.data);
      
      if (response.data && (response.data.success || response.data.message === 'Recruiter removed successfully')) {
        // Manually filter out the removed recruiter from the current state
        // This ensures the UI updates even if fetchRecruiters doesn't immediately reflect the change
        setRecruiters(prevRecruiters => prevRecruiters.filter(recruiter => recruiter.id !== recruiterId));
        
        // Refresh the recruiters list and subscription details
        await fetchRecruiters();
        await fetchSubscriptionDetails();
        return true;
      } else {
        setError(response.data?.message || 'Failed to remove recruiter');
        return false;
      }
    } catch (err) {
      console.error('Failed to remove recruiter:', err);
      
      // If we get a 404, it means the recruiter doesn't exist anymore
      if (err.response && err.response.status === 404) {
        // Manually remove the recruiter from the list
        setRecruiters(prevRecruiters => prevRecruiters.filter(recruiter => recruiter.id !== recruiterId));
        await fetchSubscriptionDetails();
        return true;
      }
      
      const errorMessage = err.response?.data?.message || 'Failed to remove recruiter';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authUser, fetchRecruiters, fetchSubscriptionDetails]);

  // Function to get recruiter performance metrics
  const getRecruiterPerformance = useCallback(async (recruiterId) => {
    if (!isAuthenticated || authUser?.userType !== 'employer') {
      return null;
    }
    
    try {
      // Get the time range from URL query params or default to 'week'
      const urlParams = new URLSearchParams(window.location.search);
      const timeRange = urlParams.get('timeRange') || 'week';
      
      // First try to get performance data from the new performance API
      try {
        logger.debug(`Getting performance data for recruiter ${recruiterId} with timeRange ${timeRange}`);
        
        // Use the new performance API endpoint with recruiter ID
        const response = await api.get(`/performance/data?timeRange=${timeRange}&recruiterId=${recruiterId}`);
        
        // Log the full response for debugging
        logger.debug('API response:', response);
        
        // Handle different response structures
        if (response.data && response.data.success && response.data.data) {
          // New API response structure with success and data wrapper
          logger.debug('Got success response with data wrapper:', response.data.data);
          return response.data.data; // Return the data directly
        } else if (response.data && response.data.performance) {
          // Original expected format with performance field
          logger.debug('Got performance data from API (original format):', response.data.performance);
          return response.data.performance;
        } else if (response.data && response.data.success) {
          // Direct success response without nested performance object
          logger.debug('Got direct success response:', response.data);
          return response.data;
        }
      } catch (apiError) {
        console.error('Error getting performance data from API:', apiError);
        // Fall through to use the data from recruiters.json
      }
      
      // If the performance API fails, fall back to getting data from recruiters.json
      const response = await api.get(`/recruiters/${recruiterId}/performance?timeRange=${timeRange}`);
      
      // Handle different response structures
      if (response.data && response.data.success && response.data.data) {
        // New API response structure with success and data wrapper
        if (response.data.data.performance) {
          logger.debug('Got performance data from recruiters API:', response.data.data.performance);
          return response.data.data.performance;
        }
      } else if (response.data && response.data.performance) {
        // Original expected format
        logger.debug('Got performance data from recruiters API (original format):', response.data.performance);
        return response.data.performance;
      }
      
      // If API doesn't return performance data, generate mock data
      logger.debug('Generating mock performance data');
      const now = new Date();
      const mockLoginHours = {
        total: Math.floor(Math.random() * 100) + 20, // 20-120 hours total
        thisMonth: Math.floor(Math.random() * 40) + 10, // 10-50 hours this month
        thisWeek: Math.floor(Math.random() * 20) + 5, // 5-25 hours this week
        today: Math.floor(Math.random() * 8) + 1, // 1-9 hours today
        loginHistory: [
          // Generate some mock login history for the past week
          ...Array(5).fill(0).map((_, i) => {
            const loginDate = new Date(now);
            loginDate.setDate(loginDate.getDate() - i);
            const loginTime = new Date(loginDate);
            loginTime.setHours(9, 0, 0, 0); // 9:00 AM
            
            const logoutTime = new Date(loginDate);
            logoutTime.setHours(17, 30, 0, 0); // 5:30 PM
            
            return {
              loginTime: loginTime.toISOString(),
              logoutTime: logoutTime.toISOString(),
              duration: 8.5 * 3600, // 8.5 hours in seconds
            };
          })
        ]};
      
      return {
        ...response.data?.performance || {},
        loginHours: mockLoginHours
      };
    } catch (err) {
      console.error('Failed to get recruiter performance:', err);
      return null;
    }
  }, [isAuthenticated, authUser]);

  // Function to clear errors
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <RecruiterContext.Provider value={{ 
      recruiters,
      loading,
      error,
      availableSeats,
      totalSeats,
      fetchRecruiters,
      inviteRecruiter,
      removeRecruiter,
      getRecruiterPerformance,
      clearError
    }}>
      {children}
    </RecruiterContext.Provider>
  );
};

// Custom hook to use the RecruiterContext
export const useRecruiters = () => {
  const context = useContext(RecruiterContext);
  if (!context) {
    throw new Error('useRecruiters must be used within a RecruiterProvider');
  }
  return context;
};
